import { useState, useEffect } from "react";
import {
  Drawer,
  Select,
  DatePicker,
  Button,
  Space,
  Input,
  Row,
  Col,
  Form,
  Divider,
  Checkbox,
} from "antd";
import {
  FilterOutlined,
  ClearOutlined,
  SearchOutlined,
  CalendarOutlined,
  CloseOutlined,
  DollarOutlined,
  UserOutlined,
  EnvironmentOutlined,
  TagOutlined,
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";

const { RangePicker } = DatePicker;
const { Option } = Select;

const AdvancedFiltersPanel = ({
  visible,
  open,
  onClose,
  onApply,
  onReset,
  dashboardType = "default",
  initialFilters = {},
}) => {
  const isDrawerOpen = open !== undefined ? open : visible;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [tripSources, setTripSources] = useState([]);
  const [salesMembers, setSalesMembers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    if (isDrawerOpen) {
      fetchFilterOptions();
      if (initialFilters && Object.keys(initialFilters).length > 0) {
        form.setFieldsValue(initialFilters);
      }
    }
  }, [isDrawerOpen, initialFilters]);

  const fetchFilterOptions = async () => {
    try {
      setLoading(true);

      const defaultDestinations = [
        { value: "Goa", label: "Goa" },
        { value: "Kerala", label: "Kerala" },
        { value: "Rajasthan", label: "Rajasthan" },
        { value: "Himachal Pradesh", label: "Himachal Pradesh" },
        { value: "Kashmir", label: "Kashmir" },
        { value: "Uttarakhand", label: "Uttarakhand" },
        { value: "Karnataka", label: "Karnataka" },
        { value: "Maharashtra", label: "Maharashtra" },
        { value: "Andaman", label: "Andaman" },
        { value: "Leh Ladakh", label: "Leh Ladakh" },
        { value: "International", label: "International" },
      ];

      const defaultSources = [
        { value: "website", label: "Website" },
        { value: "whatsapp", label: "WhatsApp" },
        { value: "facebook", label: "Facebook Ads" },
        { value: "google", label: "Google Ads" },
        { value: "referral", label: "Referral" },
        { value: "direct", label: "Direct Call" },
        { value: "email", label: "Email" },
        { value: "instagram", label: "Instagram" },
        { value: "walk_in", label: "Walk In" },
        { value: "manual", label: "Manual Entry" },
        { value: "csv_import", label: "CSV Import" },
        { value: "google_sheets", label: "Google Sheets" },
      ];

      const defaultSalesMembers = [
        { value: "user1", label: "Priya Sharma" },
        { value: "user2", label: "Amit Singh" },
        { value: "user3", label: "Neha Gupta" },
        { value: "user4", label: "Rahul Kumar" },
        { value: "user5", label: "Sneha Patel" },
        { value: "user6", label: "Vikram Joshi" },
      ];

      setTripSources(defaultSources);

      // Try fetching real destinations
      try {
        const destRes = await apiClient.get("/destinations");
        const fetchedDests = Array.isArray(destRes.data)
          ? destRes.data
          : Array.isArray(destRes.data?.data)
          ? destRes.data.data
          : [];
        if (fetchedDests.length > 0) {
          const mapped = fetchedDests.map((d) => ({
            value: d.name || d._id,
            label: d.name || d.city || "Destination",
          }));
          setDestinations(mapped);
        } else {
          setDestinations(defaultDestinations);
        }
      } catch {
        setDestinations(defaultDestinations);
      }

      // Try fetching real users
      try {
        const userRes = await apiClient.get("/users");
        const fetchedUsers = Array.isArray(userRes.data)
          ? userRes.data
          : Array.isArray(userRes.data?.data)
          ? userRes.data.data
          : [];
        if (fetchedUsers.length > 0) {
          const mappedUsers = fetchedUsers.map((u) => ({
            value: u._id,
            label: `${u.name || u.email}${u.role ? ` (${u.role})` : ""}`,
          }));
          setSalesMembers(mappedUsers);
        } else {
          setSalesMembers(defaultSalesMembers);
        }
      } catch {
        setSalesMembers(defaultSalesMembers);
      }

      if (dashboardType === "accounts") {
        setCustomers([
          { value: "alice-brown", label: "Alice Brown" },
          { value: "charlie-davis", label: "Charlie Davis" },
          { value: "diana-wilson", label: "Diana Wilson" },
          { value: "eve-johnson", label: "Eve Johnson" },
          { value: "frank-miller", label: "Frank Miller" },
        ]);

        setPaymentMethods([
          { value: "cash", label: "Cash" },
          { value: "credit_card", label: "Credit Card" },
          { value: "debit_card", label: "Debit Card" },
          { value: "bank_transfer", label: "Bank Transfer" },
          { value: "upi", label: "UPI" },
          { value: "cheque", label: "Cheque" },
        ]);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    form.validateFields().then((values) => {
      // Clean up empty values
      const cleanFilters = Object.keys(values).reduce((acc, key) => {
        if (
          values[key] !== undefined &&
          values[key] !== null &&
          values[key] !== ""
        ) {
          if (Array.isArray(values[key]) && values[key].length === 0) {
            return acc;
          }
          acc[key] = values[key];
        }
        return acc;
      }, {});

      onApply(cleanFilters);
      onClose();
    });
  };

  const handleReset = () => {
    form.resetFields();
    if (onReset) onReset();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Drawer
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Space>
            <FilterOutlined style={{ color: "#4f46e5" }} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Advanced Filters</span>
          </Space>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleClose}
            size="small"
          />
        </div>
      }
      placement="right"
      width={420}
      open={Boolean(isDrawerOpen)}
      onClose={handleClose}
      closable={false}
      footer={
        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={handleReset} icon={<ClearOutlined />}>
              Clear All
            </Button>
            <Button
              type="primary"
              onClick={handleApply}
              loading={loading}
              icon={<SearchOutlined />}
              style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
            >
              Apply Filters
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" size="middle">
        {/* ADVANCED FILTERS - SEMBARK TRAVEL CRM DESIGN */}

        {/* Trip ID / Lead ID */}
        <Form.Item name="tripId" label="Trip ID / Query ID">
          <Input placeholder="Search by Trip ID (e.g. TR-1001)..." allowClear />
        </Form.Item>

        {/* Destinations */}
        <Form.Item
          name="destinations"
          label={
            <Space>
              <EnvironmentOutlined />
              <span>Destinations</span>
            </Space>
          }
        >
          <Select
            mode="multiple"
            placeholder="Select destination(s)..."
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.children ?? "").toLowerCase().includes(input.toLowerCase()) ||
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {Array.isArray(destinations) &&
              destinations.map((dest) => (
                <Option key={dest.value} value={dest.value}>
                  {dest.label}
                </Option>
              ))}
          </Select>
        </Form.Item>

        {/* Package Cost / Budget Range */}
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="minAmount"
              label={
                <Space>
                  <DollarOutlined />
                  <span>Min Cost (₹)</span>
                </Space>
              }
            >
              <Input type="number" placeholder="Min ₹" allowClear min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="maxAmount"
              label={
                <Space>
                  <DollarOutlined />
                  <span>Max Cost (₹)</span>
                </Space>
              }
            >
              <Input type="number" placeholder="Max ₹" allowClear min={0} />
            </Form.Item>
          </Col>
        </Row>

        {/* Trip Sources */}
        <Form.Item name="tripSources" label="Trip Sources">
          <Select
            mode="multiple"
            placeholder="Select source(s)..."
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {Array.isArray(tripSources) &&
              tripSources.map((source) => (
                <Option key={source.value} value={source.value}>
                  {source.label}
                </Option>
              ))}
          </Select>
        </Form.Item>

        {/* Priority */}
        <Form.Item name="priority" label="Priority">
          <Select placeholder="Select priority..." allowClear>
            <Option value="urgent">Urgent</Option>
            <Option value="high">High</Option>
            <Option value="medium">Medium</Option>
            <Option value="low">Low</Option>
          </Select>
        </Form.Item>

        {/* Tags */}
        <Form.Item
          name="tags"
          label={
            <Space>
              <TagOutlined />
              <span>Tags / Trip Types</span>
            </Space>
          }
        >
          <Select
            mode="multiple"
            placeholder="Search & select tag(s)..."
            allowClear
            showSearch
          >
            <Option value="honeymoon">Honeymoon</Option>
            <Option value="family">Family Trip</Option>
            <Option value="adventure">Adventure</Option>
            <Option value="business">Business Travel</Option>
            <Option value="group">Group Tour</Option>
            <Option value="luxury">Luxury</Option>
            <Option value="budget">Budget</Option>
            <Option value="weekend">Weekend Getaway</Option>
          </Select>
        </Form.Item>

        {/* Sales and Ops owners */}
        <Form.Item
          name="salesOwners"
          label={
            <Space>
              <UserOutlined />
              <span>Sales & Ops Owners</span>
            </Space>
          }
        >
          <Select
            mode="multiple"
            placeholder="Search & select owner(s)..."
            allowClear
            showSearch
          >
            {Array.isArray(salesMembers) &&
              salesMembers.map((member) => (
                <Option key={member.value} value={member.value}>
                  {member.label}
                </Option>
              ))}
          </Select>
        </Form.Item>

        {/* Live During */}
        <Form.Item
          name="liveDuring"
          label={
            <Space>
              <CalendarOutlined />
              <span>Live During Travel</span>
            </Space>
          }
        >
          <RangePicker
            style={{ width: "100%" }}
            placeholder={["DD MMM'YY", "DD MMM'YY"]}
            format="DD MMM'YY"
            allowClear
          />
        </Form.Item>

        {/* Start-Date Between */}
        <Form.Item
          name="startDateBetween"
          label={
            <Space>
              <CalendarOutlined />
              <span>Travel Start-Date Between</span>
            </Space>
          }
        >
          <RangePicker
            style={{ width: "100%" }}
            placeholder={["DD MMM'YY", "DD MMM'YY"]}
            format="DD MMM'YY"
            allowClear
          />
        </Form.Item>

        {/* End-Date Between */}
        <Form.Item
          name="endDateBetween"
          label={
            <Space>
              <CalendarOutlined />
              <span>Travel End-Date Between</span>
            </Space>
          }
        >
          <RangePicker
            style={{ width: "100%" }}
            placeholder={["DD MMM'YY", "DD MMM'YY"]}
            format="DD MMM'YY"
            allowClear
          />
        </Form.Item>

        {/* Created Between */}
        <Form.Item
          name="createdBetween"
          label={
            <Space>
              <CalendarOutlined />
              <span>Created Between</span>
            </Space>
          }
        >
          <RangePicker
            style={{ width: "100%" }}
            placeholder={["DD MMM'YY", "DD MMM'YY"]}
            format="DD MMM'YY"
            allowClear
          />
        </Form.Item>

        {/* Has Due Payments */}
        <Form.Item name="hasDuePayments" valuePropName="checked">
          <Checkbox>Has Due Payments</Checkbox>
        </Form.Item>

        {/* Show Archived */}
        <Form.Item name="showArchived" valuePropName="checked">
          <Checkbox>Show Archived</Checkbox>
        </Form.Item>

        {/* Accounts Dashboard Specific Filters */}
        {dashboardType === "accounts" && (
          <>
            <Divider />

            {/* Payment Method */}
            <Form.Item name="paymentMethod" label="Payment Method">
              <Select
                mode="multiple"
                placeholder="Select payment methods..."
                allowClear
              >
                {Array.isArray(paymentMethods) &&
                  paymentMethods.map((method) => (
                    <Option key={method.value} value={method.value}>
                      {method.label}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            {/* Customers */}
            <Form.Item name="customers" label="Customers">
              <Select
                mode="multiple"
                placeholder="Select customers..."
                allowClear
              >
                {Array.isArray(customers) &&
                  customers.map((customer) => (
                    <Option key={customer.value} value={customer.value}>
                      {customer.label}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            {/* Amount Range */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="minAmount" label="Min Amount (₹)">
                  <Input type="number" placeholder="Min ₹" allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxAmount" label="Max Amount (₹)">
                  <Input type="number" placeholder="Max ₹" allowClear />
                </Form.Item>
              </Col>
            </Row>

            {/* Date Ranges */}
            <Form.Item name="invoiceDateRange" label="Invoice Date Range">
              <RangePicker
                style={{ width: "100%" }}
                placeholder={["From Date", "To Date"]}
                format="DD MMM'YY"
                allowClear
              />
            </Form.Item>

            <Form.Item name="dueDateRange" label="Due Date Range">
              <RangePicker
                style={{ width: "100%" }}
                placeholder={["From Date", "To Date"]}
                format="DD MMM'YY"
                allowClear
              />
            </Form.Item>
          </>
        )}

        {/* Additional Filters for Default / Queries Dashboard */}
        {dashboardType !== "accounts" && (
          <>
            <Divider />

            {/* Customer Name */}
            <Form.Item name="customerName" label="Customer Name">
              <Input placeholder="Search by customer name..." allowClear />
            </Form.Item>

            {/* Phone Number */}
            <Form.Item name="phoneNumber" label="Phone Number">
              <Input placeholder="Search by phone number..." allowClear />
            </Form.Item>

            {/* Trip Status */}
            <Form.Item name="status" label="Trip Status">
              <Select mode="multiple" placeholder="Select status..." allowClear>
                <Option value="new">New</Option>
                <Option value="contacted">Contacted</Option>
                <Option value="qualified">Qualified</Option>
                <Option value="quoted">Quoted</Option>
                <Option value="negotiation">In Negotiation</Option>
                <Option value="converted">Converted / Confirmed</Option>
                <Option value="lost">Lost</Option>
                <Option value="cancelled">Cancelled</Option>
                <Option value="archived">Archived</Option>
                <Option value="dropped">Dropped</Option>
              </Select>
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default AdvancedFiltersPanel;
